import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import { Radio, Segmented, Select, Switch } from 'antd';
import { Field, FieldArray, Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseIcon } from 'src/icons';
import {
  appointmentTypesAction,
  getAllPractitionersNames,
  getConditionByAppointmentType,
  settingsOrgFlag,
} from 'src/store/actions/appointment.action';
import { getSRTypeList } from 'src/modules/SuperAdmin/store/actions/service_request';
import {
  addPractitionerSettings,
  createSettingsAction,
  getSettingsNotifications,
  listSettingsPractitionersAction,
  getV3NotificationsDetailsAction,
  createV3SettingsAction,
} from 'src/store/actions/settings.action';
import {
  getAppointmentByFilterSlice,
  getAppointmentsByStatusSlice,
  getConditionByAppointmentTypeSlice,
} from 'src/store/slices/appointments.slice';
import { Button } from '../baseComponents';
import { AppointmentType } from '../baseComponents/AppointmentType';
import Organization from './Organization.page';
import AmbientAISettings from 'src/modules/ambient_solo/components/AmbientSettings/Settings';
import TemplateAdminPage from '../../modules/ambient_solo/components/TemplateAdmin/TemplateAdmin.page';
import DotPhraseAdminPage from '../../modules/ambient_solo/components/TemplateAdmin/DotPhraseAdmin.page';
import EncounterCSVUpload from '../../modules/ambient_solo/components/TemplateAdmin/EncounterCSVUpload.page';
import EHRIntegrations from '../../modules/ambient_solo/components/TemplateAdmin/EHRIntegrations.page';
import EncounterReport from '../../modules/ambient_solo/components/EncounterReport/EncounterReport.page';
import PreBuiltTemplateAdminPage from '../../modules/ambient_solo/components/PreBuiltTemplateAdmin/PreBuiltTemplateAdmin.page';
import { Billing } from 'src/modules/ambient_solo/components/Billing/Billing';
import DataArchivalSetting from '../../modules/ambient_solo/components/TemplateAdmin/DataArchivalSetting.page';
import Users from 'src/modules/ambient_solo/components/Users/Users';
import * as Yup from 'yup';
import { analytics } from 'src/services';
import ServiceRequestsCSVUpload from './ServiceRequestsCSVUpload';
import Reports from 'src/modules/appointments/components/Reports';
import EHRTemplateMapping from 'src/modules/ambient_solo/components/Settings/EHRTemplateMapping';
import { useIsOP } from 'src/hooks/misc/useTenant';

const validationSchema = Yup.object().shape({
  notifications: Yup.array().of(
    Yup.object().shape({
      number_of: Yup.number()
        .required('Required')
        .min(1, 'Must be greater than 0')
        .typeError('Must be a number'),
      duration: Yup.string()
        .required('Required')
        .test('not-empty', 'Duration cannot be empty', (value) => value !== ''),
    })
  ),
});

const srValidationSchema = Yup.object().shape({
  srNotifications: Yup.array().of(
    Yup.object().shape({
      number_of: Yup.number()
        .required('Required')
        .min(1, 'Must be greater than 0')
        .typeError('Must be a number'),
      duration: Yup.string()
        .required('Required')
        .test('not-empty', 'Duration cannot be empty', (value) => value !== ''),
    })
  ),
});

const Settings = ({ isMinimized }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const practitioner_payload = useMemo(() => {
    return {
      is_new_appointment: false,
    };
  }, []);

  useEffect(() => {
    dispatch(settingsOrgFlag());
  }, [dispatch]);

  useEffect(() => {
    dispatch(appointmentTypesAction());
    dispatch(getAppointmentByFilterSlice([]));
    dispatch(getAppointmentsByStatusSlice([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllPractitionersNames(practitioner_payload));
    dispatch(listSettingsPractitionersAction());
  }, [dispatch, practitioner_payload]);

  const nameOptions = [
    { value: 'Hours', name: 'Hours' },
    { value: 'Days', name: 'Days' },
    { value: 'Weeks', name: 'Weeks' },
  ];

  const singularNameOptions = [
    { value: 'Hour', name: 'Hour' },
    { value: 'Day', name: 'Day' },
    { value: 'Week', name: 'Week' },
  ];

  const {
    allPractitionersNames,
    getConditionsbyType,
    appointmentTypeValues,
    conditionsByPractitionerLoading,
    settingsOrgValue,
  } = useSelector((state) => state?.appointmentState);

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const { srTypeList } = useSelector((state) => state?.serviceRequestState);

  const { isEntitled: isFollowup } = useFeatureEntitlements('followup');
  const { isEntitled: isIntake } = useFeatureEntitlements('intake');
  const { isEntitled: hasAmbientAccess } = useFeatureEntitlements('ambient');
  const { isEntitled: hasReferralsAccess } = useFeatureEntitlements('referral');
  const { isEntitled: hasPhoneTriageAccess } =
    useFeatureEntitlements('phone-triage');
  const { isEntitled: hasBulkEncounterUploadAccess } = useFeatureEntitlements(
    'bulk-encounter-upload'
  );
  const { isEntitled: hasBulkEncounterUploadAccessRole } =
    usePermissionEntitlements('bulk-encounter-upload-access');

  const { isEntitled: hasEncounterReportAccess } =
    useFeatureEntitlements('encounter-report');
  const { isEntitled: hasEncounterReportAccessRole } =
    usePermissionEntitlements('encounter-report-access');

  const { isEntitled: hasTemplateAdminAccess } =
    useFeatureEntitlements('template-admin');
  const { isEntitled: hasTemplateAdminAccessRole } = usePermissionEntitlements(
    'template-admin-access'
  );

  const { isEntitled: isEHRSettingsAllowed } = useFeatureEntitlements(
    'drchrono-ehr-settings'
  );

  const { isEntitled: isDataArchiveAllowed } = useFeatureEntitlements(
    'custom-data-retention'
  );

  const { isEntitled: isDataArchiveDeletingUserAllowed } =
    useFeatureEntitlements('custom-data-retention-deleting-user');

  const { isEntitled: canViewBilling } =
    usePermissionEntitlements('billing.read');
  const { isEntitled: canViewUsers } = usePermissionEntitlements('user.read');

  const isOP = useIsOP();

  const {
    listPractitionerNames,
    getNotificationsDetails,
    getV3NotificationsDetails,
  } = useSelector((state) => state?.appointmentSettingsState);
  const [storeNotificationDetails, setStoreNotificationDetails] = useState([]);
  const [storeV3NotificationDetails, setStoreV3NotificationDetails] = useState(
    []
  );
  const [conditionByType, setConditionByType] = useState([]);
  const [storePractitionerId, setStorePractitionerId] = useState(null);
  const [storeConditionId, setStoreConditionId] = useState(null);
  const [allPractitionersDropdown, setAllPractitionersDropdown] = useState([]);
  const [updatedEmailsArray, setUpdatedEmailsArray] = useState([]);
  const [notifications, setNotifications] = useState([
    { preference: '', number_of: '', duration: '' },
  ]);

  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [storeRadioButton, setStoreRadioButton] = useState(1);
  const [settingsPractitioners, setSettingsPractitioners] = useState([]);
  const [storePractitionerEmail, setStorePractitionerEmail] = useState('');
  const [is_Practitioner_mail, setIs_Practitioner_mail] = useState(null);
  const [practitioner_Email_Show, set_Practitioner_Email_Show] = useState(null);
  const [muteNotifications, setMuteNotifications] = useState(false);
  const [muteSrNotifications, setMuteSrNotifications] = useState(false);
  const [allEmails, setAllEmails] = useState([]);

  const { currentPractitionerEntitlements } = useSelector(
    (state) => state?.practitionerState
  );
  const isAuraPro = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'pro'
  );
  const isAuraTeams = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'teams'
  );
  const isPLGUser = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'plg'
  );

  const [storeServiceRequestTypeId, setStoreServiceRequestTypeId] =
    useState(null);

  const [srNotifications, setSrNotifications] = useState([
    { preference: '', number_of: '', duration: '' },
  ]);

  const isAdmin = settingsOrgValue?.roles?.some((role) =>
    role?.toLowerCase()?.includes('admin')
  );

  const showSchedulingTab =
    isAdmin &&
    (isAuraPro ||
      isAuraTeams ||
      isPLGUser ||
      isIntake ||
      isFollowup ||
      hasReferralsAccess ||
      hasPhoneTriageAccess);

  useEffect(() => {
    setStoreNotificationDetails(getNotificationsDetails);
    set_Practitioner_Email_Show(
      getNotificationsDetails?.response?.send_practitioner_emails
    );
    setMuteNotifications(getNotificationsDetails?.response?.mute_notifications);
  }, [getNotificationsDetails]);

  useEffect(() => {
    if (getV3NotificationsDetails) {
      setStoreV3NotificationDetails(getV3NotificationsDetails);
      setMuteSrNotifications(
        getV3NotificationsDetails?.response?.mute_notifications || false
      );
    }
  }, [getV3NotificationsDetails]);

  let tabs = [];

  if (hasAmbientAccess) {
    tabs.push({
      label: 'Note Settings',
    });
  }
  if (canViewUsers && (!isIntake || !isFollowup)) {
    tabs.push({
      label: 'Users',
    });
  }

  if (
    canViewBilling &&
    !(isIntake || isFollowup || hasReferralsAccess || hasPhoneTriageAccess)
  ) {
    tabs.push({
      label: 'Billing',
    });
  }
  if (isAdmin && (isIntake || isFollowup || hasReferralsAccess)) {
    tabs.push({
      label: 'Notifications',
    });
    tabs.push({
      label: 'Reports',
    });
  }

  if (showSchedulingTab) {
    tabs.push({
      label: 'Scheduling',
    });
  }
  if (hasBulkEncounterUploadAccessRole && hasBulkEncounterUploadAccess) {
    tabs.push({
      label: 'Encounter CSV Upload',
    });
  }
  if (hasReferralsAccess) {
    tabs.push({
      label: 'CSV Upload',
    });
  }
  if (hasTemplateAdminAccessRole && hasTemplateAdminAccess) {
    tabs.push({
      label: 'Template admin',
    });
    tabs.push({
      label: 'Dot phrase admin',
    });
    tabs.push({
      label: 'Prebuilt template admin',
    });
  }
  if (hasEncounterReportAccessRole && hasEncounterReportAccess) {
    tabs.push({
      label: 'Encounter report',
    });
  }
  if (isEHRSettingsAllowed) {
    tabs.push({
      label: 'EHR integration',
    });
  }

  // if (isOP) {
  //   tabs.push({
  //     label: 'OP Template Mapping',
  //   });
  // }

  if (isDataArchiveAllowed && isAdmin) {
    tabs.push({
      label: 'Data Retention',
    });
  }

  const [activeButton, setActiveButton] = useState(tabs[0]?.label);

  useEffect(() => {
    if (
      hasReferralsAccess &&
      currentPractitioner?.org_uuid &&
      activeButton === 'Notifications'
    ) {
      dispatch(getSRTypeList(currentPractitioner?.org_uuid));
    }
  }, [
    dispatch,
    hasReferralsAccess,
    currentPractitioner?.org_uuid,
    activeButton,
  ]);

  useEffect(() => {
    if (allPractitionersNames && Array.isArray(allPractitionersNames)) {
      let dataArr = [];
      allPractitionersNames.forEach((key) => {
        dataArr.push({
          value: key?.practitioner_id,
          label: `${
            key?.practitioner_title ? key.practitioner_title + '. ' : ''
          }${key?.practitioner_name}`,
        });
      });
      setAllPractitionersDropdown(dataArr);
    }
  }, [allPractitionersNames]);

  useEffect(() => {
    if (listPractitionerNames && Array.isArray(listPractitionerNames)) {
      let dataArr = [];
      listPractitionerNames.forEach((key) => {
        dataArr.push({
          value: key?.practitioner_id,
          label: `${
            key?.practitioner_title ? key?.practitioner_title + '.' : ''
          }
                    ${key?.practitioner_name}`,
          is_checked: key?.is_shown,
          email: key?.practitioner_email,
        });
      });
      setSettingsPractitioners(dataArr);
    }
  }, [listPractitionerNames]);

  useEffect(() => {
    if (
      getConditionsbyType?.conditions &&
      Array.isArray(getConditionsbyType?.conditions)
    ) {
      let dataArr = [];
      getConditionsbyType?.conditions.forEach((key) => {
        dataArr.push({
          value: key?.condition_id,
          label: key?.condition_name,
        });
      });
      setConditionByType(dataArr);
    }
    practitioner_Email_Show &&
      setStorePractitionerEmail(getConditionsbyType?.practitioner_emails);
  }, [
    getConditionsbyType?.conditions,
    getConditionsbyType?.practitioner_emails,
    practitioner_Email_Show,
  ]);

  const srTypeOptions = useMemo(() => {
    if (srTypeList && Array.isArray(srTypeList)) {
      return srTypeList.map((item) => ({
        value: item.service_request_type_id,
        label: item.service_request_type,
      }));
    }
    return [];
  }, [srTypeList]);

  const singularDuration = (duration) => {
    switch (duration) {
      case 'Hours':
        return 'Hour';
      case 'Days':
        return 'Day';
      case 'Weeks':
        return 'Week';
      default:
        return duration;
    }
  };

  useEffect(() => {
    if (
      storeNotificationDetails?.response?.preferences?.notification_preference
    ) {
      const notificationDetailsArray =
        storeNotificationDetails?.response?.preferences?.notification_preference?.map(
          (notification) => ({
            number_of: notification?.number_of,
            duration:
              notification?.number_of === 1
                ? singularDuration(notification?.duration)
                : notification?.duration,
          })
        );
      setNotifications([...notificationDetailsArray]);
    }
    storeNotificationDetails?.response?.preferences?.emails &&
      setUpdatedEmailsArray([
        // eslint-disable-next-line no-unsafe-optional-chaining
        ...storeNotificationDetails?.response?.preferences?.emails.map(
          (emailObj) => emailObj
        ),
      ]);
  }, [storeNotificationDetails]);

  useEffect(() => {
    if (
      storeServiceRequestTypeId &&
      storeV3NotificationDetails?.response?.preferences?.notification_preference
    ) {
      const notificationDetailsArray =
        storeV3NotificationDetails?.response?.preferences?.notification_preference?.map(
          (notification) => ({
            number_of: notification?.number_of,
            duration:
              notification?.number_of === 1
                ? singularDuration(notification?.duration)
                : notification?.duration,
          })
        );
      setSrNotifications([...notificationDetailsArray]);
    } else {
      setSrNotifications([{ preference: '', number_of: '', duration: '' }]);
    }
  }, [storeV3NotificationDetails, storeServiceRequestTypeId]);

  useEffect(() => {
    if (appointmentTypeValues && Array.isArray(appointmentTypeValues)) {
      const dataArr = appointmentTypeValues.map((item) => ({
        value: item?.appointment_type_id,
        label: item?.appointment_type_text,
      }));
      setAppointmentTypes(dataArr);
    }
  }, [appointmentTypeValues]);

  useEffect(() => {
    setAllEmails([...updatedEmailsArray]);
  }, [updatedEmailsArray]);

  const initialValues = {
    notifications:
      notifications.length > 0
        ? notifications
        : [{ preference: '', number_of: '', duration: '' }],
    provider_email_Field: '',
  };

  const srInitialValues = {
    srNotifications:
      srNotifications.length > 0
        ? srNotifications
        : [{ preference: '', number_of: '', duration: '' }],
  };

  const updatePreferences = () => {
    const updatedNotifications = notifications.map((notification, index) => ({
      ...notification,
      preference: index + 1,
    }));
    setNotifications([...updatedNotifications]);
  };

  const updateSrPreferences = () => {
    const updatedSrNotifications = srNotifications.map(
      (notification, index) => ({
        ...notification,
        preference: index + 1,
      })
    );
    setSrNotifications([...updatedSrNotifications]);
  };

  const handleRemoveEmail = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    setUpdatedEmailsArray((prevEmails) => {
      const updatedEmails = [...prevEmails];
      updatedEmails.splice(index, 1);
      return updatedEmails;
    });
  };

  useEffect(() => {
    if (activeButton === 'Notifications') {
      dispatch(getAllPractitionersNames(practitioner_payload));
      dispatch(listSettingsPractitionersAction());
    } else if (activeButton === 'Scheduling') {
      dispatch(getAllPractitionersNames(practitioner_payload));
      dispatch(listSettingsPractitionersAction());
      dispatch(settingsOrgFlag());
    } else {
      dispatch(settingsOrgFlag());
    }
  }, [activeButton, dispatch, practitioner_payload]);

  const handleSegmentChange = (segment) => {
    setStoreNotificationDetails([]);
    setActiveButton(segment);
    setStoreRadioButton(1);
    setStorePractitionerId(undefined);
    setStoreConditionId(undefined);
    set_Practitioner_Email_Show(null);
    setNotifications([{ number_of: '', duration: '' }]);
    setUpdatedEmailsArray([]);
    setSrNotifications([{ preference: '', number_of: '', duration: '' }]);
    setStoreServiceRequestTypeId(null);
    setStoreV3NotificationDetails([]);
  };

  const filteredEmails = allEmails.filter((email) => email);

  const handleSubmit = (values) => {
    analytics.track('Clicked Save After Adding Notifications', {
      email: user?.email,
    });
    const notification_schedule_payload = values.notifications.map(
      ({ duration, number_of }, index) => ({
        duration,
        number_of,
        preference: index + 1,
      })
    );
    filteredEmails.includes(storePractitionerEmail)
      ? setIs_Practitioner_mail(true)
      : setIs_Practitioner_mail(false);

    const final_payload = {
      practitioner_id: storePractitionerId,
      condition_id: storeConditionId,
      send_practitioner_emails: is_Practitioner_mail,
      mute_notifications: muteNotifications,
      notification: {
        schedule:
          notification_schedule_payload[0]?.duration === '' ||
          notification_schedule_payload[0]?.number_of === ''
            ? []
            : notification_schedule_payload,
        emails: allEmails.length > 0 ? allEmails : [],
      },
    };
    dispatch(
      createSettingsAction({
        payload: final_payload,
      })
    );
  };

  const handleSubmitSr = (values) => {
    const notification_schedule_payload = values.srNotifications.map(
      ({ duration, number_of }, index) => ({
        duration,
        number_of,
        preference: index + 1,
      })
    );

    const final_payload = {
      service_request_type_id: storeServiceRequestTypeId,
      mute_notifications: muteSrNotifications,
      schedule:
        notification_schedule_payload[0]?.duration === '' ||
        notification_schedule_payload[0]?.number_of === ''
          ? []
          : notification_schedule_payload,
    };
    dispatch(createV3SettingsAction({ payload: final_payload }));
  };

  const isMobile = window.innerWidth < 1260;
  const allAppointmentTypes = !isEmpty(appointmentTypes)
    ? [...appointmentTypes]
    : [];

  return (
    <>
      <div className={`page-wrapper`}>
        <div className="segmented_appointment_settings">
          <Segmented
            options={tabs.map((button) => ({
              label: button.label,
              value: button.label,
            }))}
            defaultValue={activeButton}
            onChange={handleSegmentChange}
          />
        </div>

        <div className="lg:w-2/3">
          {activeButton === 'Notifications' && (isIntake || isFollowup) && (
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                handleSubmit,
                values,
                setFieldValue,
                errors,
                touched,
                resetForm,
              }) => (
                <Form onSubmit={handleSubmit} autoComplete="off">
                  <div className="bg-white px-4 py-2 rounded-2xl mt-5">
                    <div className="">
                      {isFollowup ? (
                        <div className="items-center justify-start flex-wrap gap-4">
                          <div className="text-lg font-semibold text-[#000] mb-3">
                            Appointment type
                          </div>
                          <div className="">
                            <Radio.Group
                              className=""
                              onChange={(e) => {
                                setConditionByType([]);
                                setStoreRadioButton(e.target.value);
                                setStoreConditionId(undefined);
                                setStorePractitionerId(undefined);
                                setUpdatedEmailsArray([]);
                                set_Practitioner_Email_Show(null);
                                setNotifications([
                                  {
                                    preference: '',
                                    number_of: '',
                                    duration: '',
                                  },
                                ]);
                                resetForm();
                                dispatch(
                                  getConditionByAppointmentTypeSlice(null)
                                );
                              }}
                              value={storeRadioButton}
                            >
                              {appointmentTypes.map((item) => (
                                <AppointmentType
                                  key={item.value}
                                  item={item}
                                  appointmentTypes={allAppointmentTypes}
                                />
                              ))}
                            </Radio.Group>
                          </div>
                        </div>
                      ) : (
                        ''
                      )}
                      <div className="grid xl:grid-cols-2 sm:grid-cols-1 gap-4 mt-5">
                        <div className="selector ">
                          <div className="text-[#000] font-medium ml-1 mb-1">
                            Practitioner name
                          </div>
                          <Select
                            id="practitioner_id"
                            bordered={false}
                            className=" bg-white  p-1 outline-1 outline-[#E5E8EB] outline-none font-sans h-12 flex items-center w-full focus:outline-none rounded-xl"
                            placeholder="Practitioner name"
                            optionFilterProp="label"
                            options={allPractitionersDropdown}
                            value={storePractitionerId || undefined}
                            showSearch
                            onChange={(e) => {
                              setConditionByType([]);
                              setUpdatedEmailsArray([]);
                              set_Practitioner_Email_Show(null);
                              setStorePractitionerId(e);
                              setIs_Practitioner_mail(true);
                              dispatch(
                                getConditionByAppointmentType(
                                  e,
                                  storeRadioButton,
                                  false
                                )
                              );
                              setStoreConditionId(null);
                              setNotifications({
                                preference: '',
                                number_of: '',
                                duration: '',
                              });
                              resetForm();
                              setStoreNotificationDetails([]);
                            }}
                          />
                        </div>
                        <div className="selector">
                          <div className="text-[#000] font-medium ml-1 mb-1">
                            Condition name
                          </div>
                          <Select
                            id="condition_id"
                            bordered={false}
                            name="condition_name"
                            className="bg-white outline-1 outline-[#E5E8EB] outline-none p-1 font-sans h-12 flex items-center w-full focus:outline-none rounded-xl"
                            placeholder="Condition name"
                            options={
                              conditionsByPractitionerLoading
                                ? [{ label: 'Loading...', value: '' }]
                                : conditionByType.length > 0
                                  ? conditionByType
                                  : ''
                            }
                            optionFilterProp="label"
                            showSearch
                            value={storeConditionId || undefined}
                            onChange={(e) => {
                              setStoreConditionId(e);
                              const body = {
                                practitioner_id: storePractitionerId,
                                condition_id: e,
                              };
                              dispatch(getSettingsNotifications(body));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="text-lg font-semibold text-[#000]">
                        Patient notification schedule
                      </div>
                      <div className="">
                        <FieldArray name="notifications">
                          {({ remove, push }) => (
                            <div>
                              {values.notifications?.length > 0 &&
                                values.notifications.map(
                                  (notification, index) => (
                                    <div className="modal____body" key={index}>
                                      <div
                                        className={`${
                                          isMobile
                                            ? index > 0
                                              ? 'border border-grey-700 rounded-md p-3'
                                              : ''
                                            : 'flex'
                                        } mt-2 gap-4 items-center`}
                                      >
                                        <div
                                          className={`${
                                            isMobile ? 'w-full mb-2 ' : 'w-24'
                                          } flex font-medium text-black`}
                                        >
                                          {index === 0
                                            ? 'Default'
                                            : `Reminder ${index + 1} `}
                                        </div>
                                        <div className="flex flex-col">
                                          <Field
                                            name={`notifications.${index}.number_of`}
                                            placeholder="Number"
                                            type="number"
                                            min="1"
                                            autoComplete="off"
                                            className={`${
                                              isMobile
                                                ? 'py-3 px-3 w-full mb-2'
                                                : 'w-52 p-3'
                                            }
                              outline-none outline-1 outline-[#E5E8EB] font-sans h-15 flex items-center  rounded-xl
                              ${
                                errors.notifications?.[index]?.number_of &&
                                touched.notifications?.[index]?.number_of
                                  ? 'border-red-500 border-2'
                                  : 'outline-[#E5E8EB]'
                              }`}
                                          />
                                          {errors.notifications?.[index]
                                            ?.number_of &&
                                            touched.notifications?.[index]
                                              ?.number_of && (
                                              <div className="text-red-500 text-xs mt-1">
                                                {
                                                  errors.notifications[index]
                                                    .number_of
                                                }
                                              </div>
                                            )}
                                        </div>

                                        <div
                                          className={`flex flex-col ${
                                            isMobile ? 'w-full' : 'w-52'
                                          }`}
                                        >
                                          <Field
                                            name={`notifications.${index}.duration`}
                                            component={({ field, form }) => (
                                              <Select
                                                {...field}
                                                showSearch
                                                id={`notifications.${index}.duration`}
                                                bordered={false}
                                                className={`font-sans h-12 flex w-full items-center rounded-xl outline-none outline-1
                                    ${
                                      errors.notifications?.[index]?.duration &&
                                      touched.notifications?.[index]?.duration
                                        ? 'border-red-500 border-2'
                                        : 'outline-[#E5E8EB]'
                                    }`}
                                                options={
                                                  notification?.number_of ===
                                                    1 ||
                                                  notification?.number_of ===
                                                    '1'
                                                    ? singularNameOptions
                                                    : nameOptions
                                                }
                                                placeholder="Duration"
                                                value={field.value || undefined}
                                                onChange={(value) =>
                                                  form.setFieldValue(
                                                    field.name,
                                                    value
                                                  )
                                                }
                                                status={
                                                  errors.notifications?.[index]
                                                    ?.duration &&
                                                  touched.notifications?.[index]
                                                    ?.duration
                                                    ? 'error'
                                                    : undefined
                                                }
                                              />
                                            )}
                                          />
                                          {errors.notifications?.[index]
                                            ?.duration &&
                                            touched.notifications?.[index]
                                              ?.duration && (
                                              <div className="text-red-500 text-xs mt-1">
                                                {
                                                  errors.notifications[index]
                                                    .duration
                                                }
                                              </div>
                                            )}
                                        </div>

                                        <div>
                                          {index !== 0 && (
                                            <CloseIcon
                                              className={'bell-icon'}
                                              onClick={() => {
                                                remove(index, () => {
                                                  updatePreferences();
                                                });
                                              }}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              <div className="mt-5 mb-5">
                                <div className="flex flex-row justify-between">
                                  <div
                                    className="flex bg-[#00d091] hover:bg-[#059669] hover:ring-[#059669] items-center cursor-pointer text-[#fff] text-sm tracking-normal px-3 py-2 gap-3 rounded-xl"
                                    onClick={() => {
                                      push({
                                        preference: '',
                                        duration: '',
                                        number_of: '',
                                      });
                                      // eslint-disable-next-line no-undef
                                      analytics.track(
                                        'Clicked Add More Notifications',
                                        { email: user?.email }
                                      );
                                    }}
                                  >
                                    <div>Add more notifications</div>
                                  </div>
                                  {storeConditionId !== null &&
                                    storePractitionerId !== null &&
                                    values?.notifications[0]?.number_of !==
                                      '' &&
                                    values?.notifications[0]?.duration !==
                                      '' && (
                                      <div className="flex items-center">
                                        <div>
                                          <Switch
                                            style={{
                                              backgroundColor: muteNotifications
                                                ? '#00D091'
                                                : '#00000040',
                                            }}
                                            checked={muteNotifications}
                                            onChange={(checked) =>
                                              setMuteNotifications(checked)
                                            }
                                            disabled={
                                              storeConditionId !== null &&
                                              storePractitionerId !== null &&
                                              values?.notifications[0]
                                                ?.number_of !== '' &&
                                              values?.notifications[0]
                                                ?.duration !== ''
                                                ? false
                                                : true
                                            }
                                          />
                                        </div>
                                        <div className="mx-2 text-[#000]">
                                          Mute notifications
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>
                          )}
                        </FieldArray>
                      </div>
                      <div className="flex  text-lg font-semibold mb-2 text-[#000]">
                        Provider notification emails
                      </div>
                      <div
                        className={`${
                          isMobile ? 'grid' : 'flex'
                        } items-center gap-3`}
                      >
                        <div>
                          <Field
                            bordered={false}
                            autoComplete="off"
                            name="provider_email_Field"
                            placeholder="Provider notification emails"
                            type="text"
                            role="presentation"
                            className={`${
                              isMobile ? 'w-full mb-3' : 'w-64'
                            } p-3 outline-1 outline-[#E5E8EB] font-sans h-15 flex items-center rounded-xl outline-none`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.target.value !== '') {
                                e.preventDefault();
                                e.stopPropagation();
                                const newlyaddedEmail = e.target.value.trim();
                                setUpdatedEmailsArray((prevEmails) => [
                                  ...prevEmails,
                                  newlyaddedEmail,
                                ]);
                                setFieldValue('provider_email_Field', '');
                              }
                            }}
                          />
                        </div>
                        {practitioner_Email_Show && (
                          <div
                            className={`${
                              storePractitionerEmail === ''
                                ? ''
                                : 'doctor-email-item px-3 py-1'
                            }`}
                          >
                            {storePractitionerEmail}
                            {storePractitionerEmail && (
                              <button
                                className="ml-3 text-[#000] bg-[#fff] rounded-full px-1"
                                type="button"
                                onClick={() => {
                                  setStorePractitionerEmail('');
                                  setIs_Practitioner_mail(false);
                                }}
                              >
                                {updatedEmailsArray?.length > 0 ? '✖' : ''}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 flex-wrap ">
                        {updatedEmailsArray.map((email, index) => {
                          // email.trim() !== '' finds [''] from the updatedEmailsArray
                          if (email.trim() !== '') {
                            return (
                              <div
                                key={index}
                                className="flex email-item mt-2 px-3 py-1"
                              >
                                {email}
                                <button
                                  type="cancel"
                                  className="bg-[#ccc] rounded-full px-1 ml-3"
                                  onClick={(event) =>
                                    handleRemoveEmail(event, index)
                                  }
                                >
                                  ✖
                                </button>
                              </div>
                            );
                          } else {
                            return null; // Render nothing if email is empty
                          }
                        })}
                      </div>
                    </div>

                    <div className="mt-5 border-b-2 border-[#E5E8EB]"></div>
                    <div className="flex mt-5 justify-end">
                      <Button
                        className={`flex mx-1 bg-[#00d091]  hover:bg-[#059669] hover:ring-[#059669] rounded-xl cursor-pointer  text-[#fff] text-base px-6 py-2`}
                        type={'submit'}
                        disabled={
                          storeConditionId !== null &&
                          storePractitionerId !== null &&
                          values?.notifications[0]?.number_of !== '' &&
                          values?.notifications[0]?.duration !== ''
                            ? false
                            : true
                        }
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {activeButton === 'Notifications' && hasReferralsAccess && (
            <Formik
              initialValues={srInitialValues}
              enableReinitialize
              validationSchema={srValidationSchema}
              onSubmit={handleSubmitSr}
            >
              {({
                handleSubmit: handleSrSubmit,
                values: srValues,
                errors: srErrors,
                touched: srTouched,
              }) => (
                <Form onSubmit={handleSrSubmit} autoComplete="off">
                  <div className="bg-white px-4 py-2 rounded-2xl mt-5">
                    <div className="">
                      <div className="grid xl:grid-cols-2 sm:grid-cols-1 gap-4 mt-5">
                        <div className="selector ">
                          <div className="text-[#000] font-medium ml-1 mb-1">
                            Service request type
                          </div>
                          <Select
                            id="service_request_type_id"
                            bordered={false}
                            className="mt-3 bg-white  p-1 outline-1 outline-[#E5E8EB] outline-none font-sans h-12 flex items-center w-full focus:outline-none rounded-xl"
                            placeholder="Service request type"
                            optionFilterProp="label"
                            options={srTypeOptions}
                            value={storeServiceRequestTypeId || undefined}
                            showSearch
                            onChange={(e) => {
                              setSrNotifications([
                                { preference: '', number_of: '', duration: '' },
                              ]);
                              setStoreServiceRequestTypeId(e);
                              dispatch(
                                getV3NotificationsDetailsAction({
                                  service_request_type_id: e,
                                })
                              );
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="text-lg font-semibold text-[#000]">
                        Patient notification schedule
                      </div>
                      <div className="">
                        <FieldArray name="srNotifications">
                          {({ remove, push }) => (
                            <div>
                              {srValues.srNotifications?.length > 0 &&
                                srValues.srNotifications.map(
                                  (notification, index) => (
                                    <div className="modal____body" key={index}>
                                      <div
                                        className={`${
                                          isMobile
                                            ? index > 0
                                              ? 'border border-grey-700 rounded-md p-3'
                                              : ''
                                            : 'flex'
                                        } mt-2 gap-4 items-center`}
                                      >
                                        <div
                                          className={`${
                                            isMobile ? 'w-full mb-2 ' : 'w-24'
                                          } flex font-medium text-black`}
                                        >
                                          {index === 0
                                            ? 'Default'
                                            : `Reminder ${index + 1} `}
                                        </div>
                                        <div className="flex flex-col">
                                          <Field
                                            name={`srNotifications.${index}.number_of`}
                                            placeholder="Number"
                                            type="number"
                                            min="1"
                                            autoComplete="off"
                                            className={`${
                                              isMobile
                                                ? 'py-3 px-3 w-full mb-2'
                                                : 'w-52 p-3'
                                            }
                            outline-none outline-1 outline-[#E5E8EB] font-sans h-15 flex items-center  rounded-xl
                            ${
                              srErrors.srNotifications?.[index]?.number_of &&
                              srTouched.srNotifications?.[index]?.number_of
                                ? 'border-red-500 border-2'
                                : 'outline-[#E5E8EB]'
                            }`}
                                          />
                                          {srErrors.srNotifications?.[index]
                                            ?.number_of &&
                                            srTouched.srNotifications?.[index]
                                              ?.number_of && (
                                              <div className="text-red-500 text-xs mt-1">
                                                {
                                                  srErrors.srNotifications[
                                                    index
                                                  ].number_of
                                                }
                                              </div>
                                            )}
                                        </div>

                                        <div
                                          className={`flex flex-col ${
                                            isMobile ? 'w-full' : 'w-52'
                                          }`}
                                        >
                                          <Field
                                            name={`srNotifications.${index}.duration`}
                                            component={({ field, form }) => (
                                              <Select
                                                {...field}
                                                showSearch
                                                id={`srNotifications.${index}.duration`}
                                                bordered={false}
                                                className={`font-sans h-12 flex w-full items-center rounded-xl outline-none outline-1
                                          ${
                                            srErrors.srNotifications?.[index]
                                              ?.duration &&
                                            srTouched.srNotifications?.[index]
                                              ?.duration
                                              ? 'border-red-500 border-2'
                                              : 'outline-[#E5E8EB]'
                                          }`}
                                                options={
                                                  notification?.number_of ===
                                                    1 ||
                                                  notification?.number_of ===
                                                    '1'
                                                    ? singularNameOptions
                                                    : nameOptions
                                                }
                                                placeholder="Duration"
                                                value={field.value || undefined}
                                                onChange={(value) =>
                                                  form.setFieldValue(
                                                    field.name,
                                                    value
                                                  )
                                                }
                                                status={
                                                  srErrors.srNotifications?.[
                                                    index
                                                  ]?.duration &&
                                                  srTouched.srNotifications?.[
                                                    index
                                                  ]?.duration
                                                    ? 'error'
                                                    : undefined
                                                }
                                              />
                                            )}
                                          />
                                          {srErrors.srNotifications?.[index]
                                            ?.duration &&
                                            srTouched.srNotifications?.[index]
                                              ?.duration && (
                                              <div className="text-red-500 text-xs mt-1">
                                                {
                                                  srErrors.srNotifications[
                                                    index
                                                  ].duration
                                                }
                                              </div>
                                            )}
                                        </div>

                                        <div>
                                          {index !== 0 && (
                                            <CloseIcon
                                              className={'bell-icon'}
                                              onClick={() => {
                                                remove(index, () => {
                                                  updateSrPreferences();
                                                });
                                              }}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              <div className="mt-5 mb-5">
                                <div className="flex flex-row justify-between">
                                  <div
                                    className="flex bg-[#00d091] hover:bg-[#059669] hover:ring-[#059669] items-center cursor-pointer text-[#fff] text-sm tracking-normal px-3 py-2 gap-3 rounded-xl"
                                    onClick={() => {
                                      push({
                                        preference: '',
                                        duration: '',
                                        number_of: '',
                                      });
                                      analytics.track(
                                        'Clicked Add More SR Notifications',
                                        { email: user?.email }
                                      );
                                    }}
                                  >
                                    <div>Add more notifications</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    </div>

                    <div className="mt-4 flex justify-end">
                      {storeServiceRequestTypeId !== null &&
                        srValues?.srNotifications[0]?.number_of !== '' &&
                        srValues?.srNotifications[0]?.duration !== '' && (
                          <div className="flex items-center">
                            <div>
                              <Switch
                                style={{
                                  backgroundColor: muteSrNotifications
                                    ? '#00D091'
                                    : '#00000040',
                                }}
                                checked={muteSrNotifications}
                                onChange={(checked) =>
                                  setMuteSrNotifications(checked)
                                }
                                disabled={
                                  storeServiceRequestTypeId !== null &&
                                  srValues?.srNotifications[0]?.number_of !==
                                    '' &&
                                  srValues?.srNotifications[0]?.duration !== ''
                                    ? false
                                    : true
                                }
                              />
                            </div>
                            <div className="mx-2 text-[#000]">
                              Mute notifications
                            </div>
                          </div>
                        )}
                    </div>

                    <div className="mt-5 border-b-2 border-[#E5E8EB]"></div>
                    <div className="flex mt-5 justify-end">
                      <Button
                        className={`flex mx-1 bg-[#00d091]  hover:bg-[#059669] hover:ring-[#059669] rounded-xl cursor-pointer  text-[#fff] text-base px-6 py-2`}
                        type={'submit'}
                        disabled={
                          storeServiceRequestTypeId === null ||
                          srValues?.srNotifications[0]?.number_of === '' ||
                          srValues?.srNotifications[0]?.duration === ''
                        }
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {activeButton === 'Notifications' && (
            <div className="mt-5">
              <Organization activeButton={activeButton} />
            </div>
          )}

          {activeButton === 'Scheduling' && (
            <div
              className="bg-white p-5 rounded-2xl mt-5"
              id="display_practitioner"
            >
              <div className="flex justify-between">
                <div className="text-lg font-semibold text-[#000] mb-5">
                  Display practitioner
                </div>
                <div className="text-[#000]">Hide/Show</div>
              </div>

              {settingsPractitioners.map((practitioner) => (
                <div
                  key={practitioner.value}
                  className="flex items-center justify-between mt-2"
                >
                  <div id="practitioner_name">
                    {practitioner.label}
                    {practitioner.email ? (
                      <>
                        {' '}
                        <span className="text-xs text-gray-500">
                          ({practitioner.email})
                        </span>
                      </>
                    ) : null}
                  </div>
                  <div
                    className="mx-4"
                    id={
                      practitioner.is_checked
                        ? 'switch_checked'
                        : 'switch_unchecked'
                    }
                  >
                    <Switch
                      defaultChecked={practitioner?.is_checked}
                      onChange={(e) => {
                        // eslint-disable-next-line no-undef
                        analytics.track(
                          'Clicked Radio Button In Hide/Show Practitioners',
                          { email: user?.email }
                        );
                        const payload = {
                          practitioner_id: practitioner.value,
                          is_shown: e,
                        };
                        dispatch(addPractitionerSettings(payload));
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeButton === 'Note Settings' && (
            <AmbientAISettings isMinimized={isMinimized} />
          )}
        </div>
        {activeButton === 'Template admin' && (
          <>
            <TemplateAdminPage />
          </>
        )}
        {activeButton === 'Prebuilt template admin' && (
          <>
            <PreBuiltTemplateAdminPage />
          </>
        )}
        {activeButton === 'Dot phrase admin' && <DotPhraseAdminPage />}
        {activeButton === 'Encounter CSV Upload' && <EncounterCSVUpload />}
        {activeButton === 'CSV Upload' && <ServiceRequestsCSVUpload />}
        {activeButton === 'EHR integration' && <EHRIntegrations />}
        {activeButton === 'OP Template Mapping' && <EHRTemplateMapping />}
        {activeButton === 'Reports' && <Reports />}
        {activeButton === 'Billing' && <Billing />}
        {activeButton === 'Data Retention' && (
          <DataArchivalSetting
            isDataArchiveDeletingUserAllowed={isDataArchiveDeletingUserAllowed}
          />
        )}
        {activeButton === 'Encounter report' && <EncounterReport />}
        {activeButton === 'Users' && <Users />}
      </div>
    </>
  );
};

export default Settings;
